import React, { forwardRef, memo } from 'react';
import cn from 'classnames';
import { Icon, IconName, Image } from 'components';
import { ButtonV3 } from 'components/ComponentV2';

import Avatar from 'components/Avatar';
import styles from './ImageUpload.module.scss';

export interface ImageUploadProps {
  /** Custom class name */
  className?: string;
  /** Custom class name for button */
  /** Custom style */
  style?: Record<string, unknown>;
  /** image url */
  image: string;
  //* alternate string */
  alt: string;
  //* onChange handler */
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  label?: string;
  imageSrc?: 'aws' | undefined;
  buttonIconName?: IconName;
  /** onRemove handler */
  onRemove?: React.MouseEventHandler<HTMLButtonElement>;
  /** Control visibility of the remove button */
  showRemovePhotoBtn?: boolean;
  removePhotoLabel?: string;
  /** Control visibility of the star overly */
  showBadge?: boolean;
  name?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = memo(
  forwardRef<HTMLInputElement, ImageUploadProps>(
    (
      {
        className = '', // custom class name
        style, // custom style
        image,
        alt,
        name,
        onChange,
        label,
        imageSrc,
        buttonIconName,
        onRemove,
        showRemovePhotoBtn = false,
        showBadge = false,
        removePhotoLabel,
        ...props
      },
      ref
    ) => {
      const contentClassNames = cn(styles.ImageUpload, className);

      return (
        <div
          className={contentClassNames}
          style={style}
          data-testid="ImageUpload"
        >
          {image !== 'upload-photo.png' ? (
            <Avatar
              icon={image}
              name={name || ''}
              size="xxlarge"
              altText={alt}
              className={styles.imageAvatar}
            />
          ) : (
            <Image
              src={image}
              alt={alt}
              source={imageSrc}
              className={styles.defaultImage}
            />
          )}
          {showBadge && (
            <Icon iconName="icon_star" className={styles.star} size="large" />
          )}
          <input
            type="file"
            accept="image/*"
            id="upload-image"
            ref={ref}
            hidden
            onChange={onChange}
            onClick={(e) => {
              e.currentTarget.value = '';
            }}
            {...props}
          />
          <ButtonV3
            variant="contained"
            color="secondary"
            type="button"
            className={styles.ImageUploadButtonV3}
          >
            <label htmlFor="upload-image">
              <Icon
                iconName={buttonIconName ? buttonIconName : 'edit-v2'}
                size="small"
              />
              {label}
            </label>
          </ButtonV3>

          {image && showRemovePhotoBtn && (
            <ButtonV3
              variant="contained"
              color="secondary"
              type="button"
              className={styles.ImageUploadButtonV3}
              onClick={onRemove}
            >
              <label>
                <Icon iconName="trash" size="small" />
                {removePhotoLabel || 'Remove photo'}
              </label>
            </ButtonV3>
          )}
        </div>
      );
    }
  )
);

ImageUpload.displayName = 'ImageUpload';
